import React, { useState, useEffect, useCallback } from 'react';
import Dashboard from './components/Dashboard';
import CallLogs from './components/CallLogs';
import Appointments from './components/Appointments';
import Settings from './components/Settings';
import Integrations from './components/Integrations';
import Contact from './components/Contact';
import AdminConversationDetails from './components/AdminConversationDetails';
import AgentWidget from './components/AgentWidget';
import OnboardingModal from './components/ui/OnboardingModal';
import AppShell from './components/layout/AppShell';
import LoginScreen from './components/LoginScreen';
import apiService from './services/apiService';
import { SettingsProvider } from './contexts/SettingsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { getStoredRole, setStoredRole, clearStoredRole, isAdminRole } from './utils/auth';
import './App.css';

const ADMIN_ONLY_TABS = ['conversation-details'];

function App() {
  const [authRole, setAuthRole] = useState(() => getStoredRole());
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isConnected, setIsConnected] = useState(false);
  const [health, setHealth] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isAdmin = isAdminRole(authRole);

  const handleRealTimeUpdate = useCallback((data) => {
    if (data.type === 'connected') {
      setIsConnected(true);
    } else if (data.type === 'new_call') {
      setCallLogs((prev) => [data.data, ...prev]);
    }
  }, []);

  const loadData = useCallback(async () => {
    const healthy = await apiService.isHealthy();
    setIsConnected(healthy);
    if (!healthy) {
      setHealth(null);
      return;
    }
    const [healthData, calls] = await Promise.all([
      apiService.fetchHealth(),
      apiService.fetchCalls(),
    ]);
    setHealth(healthData);
    setCallLogs(calls);
  }, []);

  useEffect(() => {
    if (!authRole) {
      setLoading(false);
      return undefined;
    }

    let cleanup;
    const init = async () => {
      setLoading(true);
      try {
        await loadData();
        if (await apiService.isHealthy()) {
          apiService.startRealTimeUpdates();
          cleanup = apiService.addUpdateListener(handleRealTimeUpdate);
        }
      } catch (error) {
        console.error('Uygulama başlatılamadı:', error);
        setIsConnected(false);
      } finally {
        setLoading(false);
      }
    };
    init();
    return () => {
      cleanup?.();
      apiService.stopRealTimeUpdates();
    };
  }, [authRole, loadData, handleRealTimeUpdate]);

  // Block non-admin access to admin-only tabs
  useEffect(() => {
    if (!isAdmin && ADMIN_ONLY_TABS.includes(activeTab)) {
      setActiveTab('dashboard');
    }
  }, [isAdmin, activeTab]);

  // Login screen always uses the dark cockpit look, regardless of saved theme.
  useEffect(() => {
    if (!authRole) {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, [authRole]);

  const handleLogin = (role) => {
    setStoredRole(role);
    setAuthRole(role);
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    clearStoredRole();
    setAuthRole(null);
    setActiveTab('dashboard');
    apiService.stopRealTimeUpdates();
  };

  const handleTabChange = (tab) => {
    if (ADMIN_ONLY_TABS.includes(tab) && !isAdmin) {
      setActiveTab('dashboard');
      return;
    }
    setActiveTab(tab);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const addTestCall = async () => {
    try {
      await apiService.testWebhook();
      await loadData();
    } catch (error) {
      console.error('Örnek çağrı eklenemedi:', error);
    }
  };

  if (!authRole) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <ThemeProvider>
        <div className="flex h-screen items-center justify-center" style={{ background: 'var(--bg-app)' }}>
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-accent-400 shadow-glow-cyan" />
            <p className="mt-4 text-slate-400">Panel yükleniyor…</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
    <SettingsProvider>
    <AppShell
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isConnected={isConnected}
      health={health}
      onRefresh={handleRefresh}
      onTestCall={addTestCall}
      isRefreshing={isRefreshing}
      isAdmin={isAdmin}
      onLogout={handleLogout}
    >
      {activeTab === 'dashboard' && <Dashboard callLogs={callLogs} />}
      {activeTab === 'call-logs' && <CallLogs callLogs={callLogs} isAdmin={isAdmin} />}
      {activeTab === 'appointments' && <Appointments />}
      {activeTab === 'settings' && <Settings />}
      {activeTab === 'integrations' && <Integrations />}
      {activeTab === 'contact' && <Contact isAdmin={isAdmin} />}
      {activeTab === 'conversation-details' && isAdmin && (
        <AdminConversationDetails callLogs={callLogs} />
      )}
      <AgentWidget />
      <OnboardingModal />
    </AppShell>
    </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
